let currentSession = null;
let sessionRequest = null;

function publishSession(info) {
  currentSession = info;
  window.dispatchEvent(new CustomEvent("admin-session-change", { detail: info }));
}

function reportError(message) {
  window.dispatchEvent(new CustomEvent("admin-session-error", { detail: message }));
}

export function getAdminSession() {
  if (!sessionRequest) {
    sessionRequest = fetch("/api/admin/session", { credentials: "same-origin", cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to verify admin login. Please try again.");
        const info = await response.json();
        if (typeof info.authenticated !== "boolean" || typeof info.csrfToken !== "string") {
          throw new Error("Invalid admin session response.");
        }
        publishSession(info);
        return info;
      })
      .finally(() => { sessionRequest = null; });
  }
  return sessionRequest;
}

export default async function apiFetch(url, options = {}) {
  if (typeof url !== "string" || !url.startsWith("/api/")) {
    throw new Error("API requests must use the same-origin /api route.");
  }
  const headers = { ...options.headers };
  const method = (options.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    let info;
    try {
      info = currentSession || await getAdminSession();
    } catch (err) {
      console.error("Admin session verification failed:", err);
      reportError(err.message);
      throw err;
    }
    headers["X-CSRF-Token"] = info.csrfToken;
  }
  let response;
  try {
    response = await fetch(url, { ...options, credentials: "same-origin", headers });
  } catch (err) {
    console.error("API request failed:", err);
    const message = "Unable to reach the server. Please try again.";
    reportError(message);
    throw new Error(message);
  }
  if (response.status >= 500) {
    const message = "Unable to complete the request. Please try again.";
    reportError(message);
    throw new Error(message);
  }
  if ([401, 403].includes(response.status) && url !== "/api/admin") {
    const message = response.status === 401
      ? "Your admin session has expired. Please log in again."
      : "Session verification failed. Please retry.";
    if (response.status === 401) publishSession(null);
    else currentSession = null;
    reportError(message);
    throw new Error(message);
  }
  if (!response.ok && url !== "/api/admin") {
    let message = `Request failed (${response.status}). Please try again.`;
    try {
      const details = await response.json();
      if (typeof details.message === "string") message = details.message;
      else if (typeof details === "string") message = details;
    } catch (err) {
      console.error("Unable to read API error response:", err);
    }
    reportError(message);
    throw new Error(message);
  }
  return response;
}

export async function loginAdmin(password) {
  const response = await apiFetch("/api/admin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    if (response.status === 403) currentSession = null;
    throw new Error(response.status === 429
      ? "Too many login attempts. Please try again in 15 minutes."
      : response.status === 401 ? "Incorrect password." : "Unable to log in. Please try again.");
  }
  const info = await response.json();
  if (info.authenticated !== true || typeof info.csrfToken !== "string") {
    throw new Error("Invalid admin login response.");
  }
  publishSession(info);
}

export async function logoutAdmin() {
  const response = await apiFetch("/api/admin/logout", { method: "POST" });
  if (!response.ok) throw new Error("Unable to log out. Please try again.");
  publishSession(null);
}
